/**
 * Where a drawing's or model's converted files come from. On prod the backend
 * hands out one wildcard-signed CloudFront folder per file
 * (GET /api/files/:fileId/cdn), so the browser fetches tabs and GLBs from the
 * nearest edge; elsewhere it answers { mode: 'origin' } and the viewers keep
 * using /api/dwg2d/* and /api/ifc3d/file.
 */

export interface CdnFolder { base: string; query: string; expiresAt: number }

// Ask again well before a signature expires, so a long session never fetches
// with a dead one.
const REFRESH_MS = 5 * 60 * 1000;

const known = new Map<string, CdnFolder | null>();
const pending = new Map<string, Promise<CdnFolder | null>>();

/** Drawings are stored gzipped and CloudFront serves them as stored: the browser has to unpack them itself. */
export const canUnpackGzip = (): boolean => typeof DecompressionStream !== 'undefined';

export const cdnAssetUrl = (folder: CdnFolder, name: string): string =>
  `${folder.base}${name.split('/').map(encodeURIComponent).join('/')}?${folder.query}`;

export function resetCdnFolders(): void {
  known.clear();
  pending.clear();
}

/** The signed folder for a file, or null to use the API routes. Never throws. */
export async function cdnFolder(
  fileId: string,
  auth: { shareToken?: string | null; headers?: Record<string, string> } = {},
): Promise<CdnFolder | null> {
  const key = `${fileId}|${auth.shareToken ?? ''}`;
  const hit = known.get(key);
  if (hit === null) return null;
  if (hit && hit.expiresAt - Date.now() > REFRESH_MS) return hit;
  let req = pending.get(key);
  if (!req) {
    req = (async () => {
      try {
        const qs = auth.shareToken ? `?shareToken=${encodeURIComponent(auth.shareToken)}` : '';
        const res = await fetch(`/api/files/${fileId}/cdn${qs}`, auth.headers ? { headers: auth.headers } : undefined);
        if (!res.ok) return null;
        const body = await res.json() as { mode?: string } & Partial<CdnFolder>;
        const folder = body.mode === 'cloudfront' && body.base && body.query && body.expiresAt
          ? { base: body.base, query: body.query, expiresAt: body.expiresAt }
          : null;
        known.set(key, folder);
        return folder;
      } catch {
        return null;
      }
    })().finally(() => pending.delete(key));
    pending.set(key, req);
  }
  return req;
}

const isGzip = (b: Uint8Array) => b.length > 2 && b[0] === 0x1f && b[1] === 0x8b;

/** A response's text, unpacking it when it arrived still gzipped (straight from CloudFront). */
export async function textOf(res: Response): Promise<string> {
  const bytes = new Uint8Array(await res.arrayBuffer());
  if (isGzip(bytes) && canUnpackGzip()) {
    return new Response(new Response(bytes).body!.pipeThrough(new DecompressionStream('gzip'))).text();
  }
  return new TextDecoder().decode(bytes);
}

export async function jsonOf<T>(res: Response): Promise<T> {
  return JSON.parse(await textOf(res)) as T;
}
